import React, { useMemo } from 'react';
import { CheckIcon, XIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Donut } from '../../components/ui/Donut';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { attendanceSummary } from '../../utils/attendance';
import { eventTitle, eventTypeClass, eventTypeShort, formatDate } from '../../utils/labels';
const donutColor: Record<string, string> = {
  zelena: '#007c69',
  oranzna: '#d97706',
  rdeca: '#e11d48',
  premalo: '#cbd5e1'
};
export function MemberAttendance() {
  const {
    currentMember,
    settings,
    past,
    attendance,
    isExpected
  } = useApp();
  const me = currentMember!;
  const povzetek = attendanceSummary(me, settings);
  const jeGost = me.type === 'gostujoci' || me.type === 'castni';
  const zgodovina = useMemo(() => past.filter(e => e.steje && isExpected(e, me.id)).slice(0, 8).map(e => ({
    event: e,
    mark: attendance[e.id]?.[me.id] ?? 'prisoten'
  })), [past, attendance, isExpected, me.id]);
  return <div className="mx-auto max-w-3xl space-y-4" data-id="element-1371" data-mp="22bbyn6dpmcuz">
      <div data-id="element-1372" data-mp="26cnkr1xdkfyf">
        <h1 className="text-2xl font-semibold text-ink" data-id="element-1373" data-mp="26c4a86a20tuf">Moja prisotnost</h1>
        <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1374" data-mp="2bbgt6fxt1grq">Sezona 2026/27</p>
      </div>

      <Card className="p-5" data-id="element-1375" data-mp="2b9465od6wg7t">
        <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center" data-id="element-1376" data-mp="2gb3bh3shpn7e">
          <Donut odstotek={povzetek.status === 'premalo' ? null : povzetek.odstotek} color={donutColor[povzetek.status]} size={112} label="prisotnost" data-id="element-1377" data-mp="2l9eiz7vbblmg" />
          <div className="flex-1" data-id="element-1378" data-mp="2u2hrr2v0jaar">
            {!jeGost && <StatusBadge status={povzetek.status} odstotek={povzetek.odstotek} showPercent={false} data-id="element-1379" data-mp="2u42qz5s9vhey" />}
            <p className="mt-2 text-sm text-ink" data-id="element-1380" data-mp="3405r663kd5z9">
              Prisotni ste bili na <span className="font-semibold" data-id="element-1381" data-mp="340fjw1fztr8n">{povzetek.prisoten}</span> od{' '}
              <span className="font-semibold" data-id="element-1382" data-mp="13yfx5tmpxhrp">{povzetek.pricakovan}</span> dogodkov, ki štejejo v
              statistiko.
            </p>
            <p className="mt-1.5 text-[13px] leading-relaxed text-ink-muted" data-id="element-1383" data-mp="ywz0697cufge">
              {jeGost ? 'Kot gostujoča članica niste vključeni v semafor prisotnosti — vaša udeležba je dobrodošla, a ni obvezna.' : povzetek.status === 'premalo' ? `Semafor se vklopi po ${settings.minDogodkov} štetih dogodkih. Za zdaj prikazujemo nevtralno stanje.` : `Meje: zelena ${settings.pragZelena} % in več, oranžna od ${settings.pragOranzna} % do ${settings.pragZelena} %, rdeča pod ${settings.pragOranzna} %.`}
            </p>
          </div>
        </div>

        {!jeGost && povzetek.status !== 'premalo' && <div className="mt-5" data-id="element-1384" data-mp="292mpd77s09e">
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-canvas" data-id="element-1385" data-mp="3teu33kv6vyj0">
              <div className={`h-full rounded-full ${povzetek.status === 'zelena' ? 'bg-green-500' : povzetek.status === 'oranzna' ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
            width: `${povzetek.odstotek ?? 0}%`
          }} data-id="element-1386" data-mp="3th6ueomu7l8q" />
              <span className="absolute top-0 h-full w-px bg-ink/30" style={{
            left: `${settings.pragZelena}%`
          }} data-id="element-1387" data-mp="2449usegelcg3" />
            </div>
            <div className="mt-1.5 flex justify-between text-[11px] text-ink-muted" data-id="element-1388" data-mp="1v66epxo6t7vo">
              <span data-id="element-1389" data-mp="1g97749tc5f17">0 %</span>
              <span data-id="element-1390" data-mp="1g9yrsple9scp">prag {settings.pragZelena} %</span>
              <span data-id="element-1391" data-mp="1l6fgpm0f4aqu">100 %</span>
            </div>
          </div>}
      </Card>

      <Card className="p-5" data-id="element-1392" data-mp="3amey1qe6x6hk">
        <h2 className="text-base font-semibold text-ink" data-id="element-1393" data-mp="3angjmosu1bdj">Zadnji dogodki</h2>
        <ul className="mt-3 divide-y divide-line" data-id="element-1394" data-mp="3flrsaw7e5fu6">
          {zgodovina.map(({
          event,
          mark
        }) => <li key={event.id} className="flex items-center gap-3 py-3" data-id="element-1395" data-mp="1fja51fkf85zi">
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${mark === 'prisoten' ? 'bg-green-50 text-green-600' : 'bg-rose-50 text-rose-600'}`} data-id="element-1396" data-mp="1bhf44c6ejum9">
                {mark === 'prisoten' ? <CheckIcon className="h-4 w-4" data-id="element-1397" data-mp="11iz9w0c2w58j" /> : <XIcon className="h-4 w-4" data-id="element-1398" data-mp="wjw7yoreaf4s" />}
              </span>
              <span className="min-w-0 flex-1" data-id="element-1399" data-mp="wm8xpo3e9tse">
                <span className="block truncate text-sm font-medium text-ink" data-id="element-1400" data-mp="rt61f878g68i">{eventTitle(event)}</span>
                <span className="block text-[13px] text-ink-muted" data-id="element-1401" data-mp="nr1cunvn5zsq">{formatDate(event.datum)}</span>
              </span>
              <Badge className={eventTypeClass[event.type]} data-id="element-1402" data-mp="iu11pyjxq6ur">{eventTypeShort[event.type]}</Badge>
            </li>)}
          {zgodovina.length === 0 && <li className="py-6 text-center text-[13px] text-ink-muted" data-id="element-1403" data-mp="8u1vi8azmsl2">Zaenkrat ni zabeleženih dogodkov.</li>}
        </ul>
      </Card>
    </div>;
}